'use client';
import {
  faFacebook,
  faInstagram,
  faLinkedinIn,
  faSnapchat,
  faTiktok,
  faXTwitter,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatText, TextRole } from '@pickleballinc/lib/format-text';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { Session } from '@pickleballinc/lib/session';
import { Badge, Button, createS3ImageStore, ImageEditor } from '@pickleballinc/react-ui';
import { RankingStatusBadge } from '@pickleballinc/ui/components/global/RankingStatusBadge/RankingStatusBadge';
import { Avatar } from '@pickleballinc/ui/components/ui/avatar';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { publicEnv } from '@/lib/env';
import { FollowActionButton } from '@/modules/players/v2/header/FollowActionButton';
import { FriendActionButtons } from '@/modules/players/v2/header/FriendActionButtons';
import { ProfileInfoBox } from '@/modules/players/v2/header/ProfileInfoBox';
import { usePlayerHeaderActions } from '@/modules/players/v2/header/usePlayerHeaderActions';
import { ImageWrapper } from '@/modules/players/v2/ImageWrapper';
import { useCreateUserImage } from '@/modules/players/v2/tabs/edit-profile/hooks/hooks';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';
import { PlayerData, UserImage } from '@/types/players.types';
import { FEATURE_FLAGS } from '@/utils/helpers/constants';
import { customRevalidateTag } from '@/utils/helpers/customRevalidateTag';

interface SponsorImage {
  imageUrl: string | null;
  link: string | null;
  name?: string | null;
}

type PlayerHeaderMobileProps = {
  playerData: PlayerData | undefined;
  headerImage: UserImage | undefined;
  profileImage: UserImage | undefined;
  sponsorImage: SponsorImage | undefined;
  isProPlayer: boolean | undefined;
  isDuprActive: boolean;
  currentUserIsProPlayer?: boolean;
  user: Session | undefined;
};

const s3ImageStore = createS3ImageStore({
  bucketPath: 'profile-images',
  uploadUrl: `${publicEnv.NEXT_PUBLIC_IMAGE_UPLOAD_URL}/request`,
});

export const PlayerHeaderMobile = ({
  playerData,
  headerImage,
  profileImage,
  sponsorImage,
  isProPlayer,
  isDuprActive,
  currentUserIsProPlayer,
  user,
}: PlayerHeaderMobileProps) => {
  const { hasPermission } = useAuth();
  const { mutate: createUserImage } = useCreateUserImage();
  const [imageUpload, setImageUpload] = useState(false);
  const sponsorLink = playerData?.sponsors?.find((sponsor) => sponsor.isPrimary === true)?.url;
  const { canSeeMyDashboard, canSeeFriendActions, canSeeFollowActions } = usePlayerHeaderActions({
    playerData,
    user,
    isProPlayer,
    currentUserIsProPlayer,
  });

  // to avoid iOS scroll bug
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className='lg:hidden'>
      <div
        className='w-full'
        style={{
          backgroundImage: `url(${getCDNURL(`${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/legacy/player-header-bg2.svg`, 1000, 1000)})`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }}
      >
        {playerData ? (
          <div className='container mx-auto flex !max-w-[996px] flex-col items-center justify-center bg-gradient-to-bl from-[#0A0026] to-transparent py-4 text-white sm:py-8'>
            <div className='flex h-fit flex-col flex-wrap items-center justify-center gap-2'>
              <div className='flex flex-col items-center font-semibold'>
                <div className='font-semibold uppercase'>
                  {playerData.firstName} {playerData?.middleName}
                </div>

                <div className='text-4xl font-bold leading-[44px] uppercase'>
                  {playerData.lastNameAndSuffix.length > 12
                    ? playerData.lastNameAndSuffix.slice(0, 12) + '...'
                    : playerData.lastNameAndSuffix}
                </div>
                <div className='flex flex-row w-full items-center justify-center gap-2'>
                  {playerData.nickname !== '' ? (
                    <div className='block font-semibold uppercase'>{`(${playerData.nickname})`}</div>
                  ) : null}
                  <div className='w-2/3'>
                    {FEATURE_FLAGS.ENABLE_RANKING_BADGES ? (
                      <RankingStatusBadge
                        badgeRank={playerData.badgeRank}
                        rankingStatus={playerData.badgeTitle}
                        showBadge={playerData.showBadgeTitle}
                        badgeCenter
                        size='sm'
                      />
                    ) : null}
                  </div>
                </div>
                <div className='mt-2 flex items-center text-center font-bold'>
                  {playerData.country !== '' && playerData.countryFlag !== '' && (
                    <>
                      <img
                        src={playerData.countryFlag}
                        width={45}
                        height={24}
                        alt='country'
                        className='mr-2'
                      />
                      <div className='pr-4'>{playerData.country}</div>
                    </>
                  )}
                  {playerData.proLevelId ? (
                    <div className='border-l pl-4'>{playerData.typeOfPlayer}</div>
                  ) : null}
                </div>
              </div>
            </div>
            <div className='mt-4 flex w-full shrink-0 justify-center'>
              {isProPlayer && headerImage ? (
                <div className='relative w-full'>
                  {headerImage && headerImage.totalApprovals < 2 && !headerImage.dateOfRejection ? (
                    <Badge
                      label='Picture pending approval'
                      size='md'
                      variation='warning'
                      className='!absolute left-2 top-2 text-center'
                    />
                  ) : null}
                  {headerImage && headerImage.dateOfRejection ? (
                    <Badge
                      label='Picture declined'
                      size='md'
                      variation='error'
                      className='!absolute left-2 top-2 text-center'
                    />
                  ) : null}
                  <div className='h-40 w-full flex-1'>
                    <ImageWrapper
                      src={getCDNURL(headerImage?.url, 358, 160)}
                      fill
                      fallbackSrc={getCDNURL(
                        `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/legacy/profile-image-pro.svg`,
                        358,
                        160
                      )}
                      className='object-contain'
                      alt={`${playerData.firstName} ${playerData?.middleName} ${playerData.lastNameAndSuffix}`}
                    />
                  </div>
                </div>
              ) : (
                <div className='relative'>
                  {profileImage &&
                  profileImage.totalApprovals < 2 &&
                  !profileImage.dateOfRejection ? (
                    <Badge
                      label='Picture pending approval'
                      size='md'
                      variation='warning'
                      className='!mb-3 w-[132px] text-center'
                    />
                  ) : null}
                  {profileImage && profileImage.dateOfRejection ? (
                    <Badge
                      label='Picture declined'
                      size='md'
                      variation='error'
                      className='!absolute -top-2 z-[1] text-center'
                    />
                  ) : null}
                  <div className='relative mb-4 size-32'>
                    <Avatar
                      firstName={playerData.firstName}
                      lastName={playerData.lastNameAndSuffix}
                      src={
                        profileImage?.url
                          ? getCDNURL(profileImage.url, 190, 190, 0, true)
                          : undefined
                      }
                      variant='transparent'
                      size='5xl'
                      fallbackDelayMs={600}
                      className='border border-white bg-experiment/60'
                    >
                      {imageUpload && !profileImage ? (
                        <div className='flex size-full items-center justify-center'>
                          {formatText({ text: 'Loading...', role: TextRole.DescriptiveText })}
                        </div>
                      ) : null}
                    </Avatar>
                  </div>
                  {(hasPermission(Permission.USERS_PROFILES_EDITOR) ||
                    playerData.uuid === user?.uuid) &&
                    !profileImage &&
                    !imageUpload && (
                      <div className='absolute bottom-[30px] right-0 cursor-pointer rounded-full '>
                        <ImageEditor
                          store={s3ImageStore}
                          token={user?.token}
                          isCircle
                          editorConfig={{
                            aspectRatio: 1,
                          }}
                          width={30}
                          height={30}
                          onImageUpload={(response) => {
                            setImageUpload(true);
                            createUserImage(
                              {
                                uuid: playerData?.uuid,
                                data: {
                                  type: 'profileImg',
                                  url: response.pintura.store.uploadURL || '',
                                  firstName: playerData.firstName,
                                  lastName: playerData.lastNameAndSuffix,
                                },
                              },
                              {
                                onSuccess: () => {
                                  customRevalidateTag('player_profile');
                                },
                                onError: () => toast.error('Something went wrong. Try again.'),
                              }
                            );
                          }}
                          dropzoneClassName='!border-solid !border-white !bg-experiment'
                        >
                          <FontAwesomeIcon icon={faPlus} className='!size-[15px]' color='white' />
                        </ImageEditor>
                      </div>
                    )}
                </div>
              )}
            </div>
            {canSeeFriendActions && user?.uuid ? (
              <div className='mb-3 flex flex-col items-center'>
                <FriendActionButtons
                  playerUuid={playerData.uuid}
                  userUuid={user!.uuid}
                  playerFirstName={playerData.firstName}
                  playerLastName={playerData.lastNameAndSuffix}
                  playerSlug={playerData.slug}
                />
              </div>
            ) : null}
            <ProfileInfoBox
              playerData={playerData}
              hasBorder
              user={user}
              isDuprActive={isDuprActive}
            />
            {playerData.proLevelId === 5 && playerData.coach ? (
              <div className='mt-2 flex items-center gap-x-2  '>
                Coach: <div className='font-bold'>{playerData.coach}</div>
              </div>
            ) : null}
            {isProPlayer ||
            (sponsorImage && sponsorImage.imageUrl) ||
            playerData.facebook ||
            playerData.instagram ||
            playerData.snapchat ||
            playerData.twitter ||
            playerData.linikedIn ||
            playerData.youtube ||
            playerData.tiktok ? (
              <div className='mt-3 flex w-full flex-col items-center justify-center'>
                {isProPlayer && sponsorImage && sponsorImage?.imageUrl ? (
                  <div className='mb-3 flex flex-col items-center'>
                    <span className='text-sm'>Sponsor</span>
                    <a
                      href={sponsorLink}
                      target='_blank'
                      aria-label={`Link to player's ${sponsorImage.name} (opens in a new tab)`}
                      rel='noreferrer'
                    >
                      <ImageWrapper
                        alt='#sponsor'
                        width={200}
                        height={200}
                        className='mt-1 h-20 w-28 rounded-lg border bg-white object-contain'
                        src={getCDNURL(sponsorImage.imageUrl, 200, 200)}
                        fill={false}
                      />
                    </a>
                  </div>
                ) : null}
                {playerData.facebook ||
                playerData.instagram ||
                playerData.snapchat ||
                playerData.twitter ||
                playerData.linikedIn ||
                playerData.youtube ||
                playerData.tiktok ? (
                  <div className='mb-4 flex flex-col items-center'>
                    <span className='text-sm'>Follow {playerData.firstName}</span>
                    <div className=''>
                      <div className='flex w-full gap-x-2 overflow-x-auto p-2'>
                        {playerData.facebook ? (
                          <a
                            href={playerData.facebook}
                            target='_blank'
                            aria-label="Link to player's Facebook (opens in a new tab)"
                            rel='noreferrer'
                          >
                            <div className='flex size-10 items-center justify-center rounded-full bg-white'>
                              <FontAwesomeIcon
                                icon={faFacebook}
                                size={'xl'}
                                color='#1877F2'
                                title='Facebook Icon'
                                titleId='facebook-icon'
                              />
                            </div>
                          </a>
                        ) : null}
                        {playerData.instagram ? (
                          <a
                            href={playerData.instagram}
                            target='_blank'
                            aria-label="Link to player's Instagram (opens in a new tab)"
                            rel='noreferrer'
                          >
                            <div className='flex size-10 items-center justify-center rounded-full bg-white'>
                              <FontAwesomeIcon
                                icon={faInstagram}
                                size={'xl'}
                                color='#000100'
                                title='Instagram Icon'
                                titleId='instagram-icon'
                              />
                            </div>
                          </a>
                        ) : null}
                        {playerData.snapchat ? (
                          <a
                            href={playerData.snapchat}
                            target='_blank'
                            aria-label="Link to player's Snapchat (opens in a new tab)"
                            rel='noreferrer'
                          >
                            <div className='flex size-10 items-center justify-center rounded-full bg-white'>
                              <FontAwesomeIcon
                                icon={faSnapchat}
                                size={'xl'}
                                color='#000100'
                                title='Snapchat Icon'
                                titleId='snapchat-icon'
                              />
                            </div>
                          </a>
                        ) : null}
                        {playerData.twitter ? (
                          <a
                            href={playerData.twitter}
                            target='_blank'
                            aria-label="Link to player's Twitter (opens in a new tab)"
                            rel='noreferrer'
                          >
                            <div className='flex size-10 items-center justify-center rounded-full bg-white'>
                              <FontAwesomeIcon
                                icon={faXTwitter}
                                size={'xl'}
                                color='#0F1419'
                                title='Twitter Icon'
                                titleId='twitter-icon'
                              />
                            </div>
                          </a>
                        ) : null}
                        {playerData.linikedIn ? (
                          <a
                            href={playerData.linikedIn}
                            target='_blank'
                            aria-label="Link to player's LinkedIn (opens in a new tab)"
                            rel='noreferrer'
                          >
                            <div className='flex size-10 items-center justify-center rounded-full bg-white'>
                              <FontAwesomeIcon
                                icon={faLinkedinIn}
                                size={'xl'}
                                color='#0A66C2'
                                title='LinkedIn Icon'
                                titleId='linkedin-icon'
                              />
                            </div>
                          </a>
                        ) : null}
                        {playerData.youtube ? (
                          <a
                            href={playerData.youtube}
                            target='_blank'
                            aria-label="Link to player's YouTube (opens in a new tab)"
                            rel='noreferrer'
                          >
                            <div className='flex size-10 items-center justify-center rounded-full bg-white'>
                              <FontAwesomeIcon
                                icon={faYoutube}
                                size={'xl'}
                                color='#c4302b'
                                title='Youtube Icon'
                                titleId='youtube-icon'
                              />
                            </div>
                          </a>
                        ) : null}
                        {playerData.tiktok ? (
                          <a
                            href={playerData.tiktok}
                            target='_blank'
                            aria-label="Link to player's TikTok (opens in a new tab)"
                            rel='noreferrer'
                          >
                            <div className='flex size-10 items-center justify-center rounded-full bg-white'>
                              <FontAwesomeIcon
                                icon={faTiktok}
                                size={'xl'}
                                color='black'
                                title='TikTok Icon'
                                titleId='tiktok-icon'
                              />
                            </div>
                          </a>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className='flex flex-row gap-2 items-center flex-wrap justify-center'>
              {(hasPermission(Permission.USERS_PROFILES_EDITOR) ||
                playerData.uuid === user?.uuid) && (
                <CustomLink href={`/players/${playerData.slug}/profile/edit`}>
                  <Button
                    variant='primary'
                    size='sm'
                    className='!mt-2 !rounded bg-experiment !px-2 !py-1 !text-sm whitespace-nowrap'
                  >
                    Edit Profile
                  </Button>
                </CustomLink>
              )}

              {canSeeMyDashboard ? (
                <CustomLink href={`/players/${playerData.slug}/v3/profile`}>
                  <Button
                    variant='primary'
                    size='sm'
                    className='!mt-2 !rounded bg-experiment !px-2 !py-1 !text-sm whitespace-nowrap'
                    data-testid={`players-button-edit-profile`}
                  >
                    My Dashboard
                  </Button>
                </CustomLink>
              ) : null}

              <CustomLink
                href={`/head-to-head?type=singles&current_page=1&team_one_player_one=${playerData.uuid}`}
              >
                <Button
                  variant='secondary'
                  size='sm'
                  className='!rounded !px-2 !py-1 !text-sm text-purple-600 hover:!text-purple-700 !mt-2 whitespace-nowrap'
                  data-testid={`players-button-head-to-head`}
                >
                  Head to Head
                </Button>
              </CustomLink>

              {canSeeFollowActions && user?.uuid ? (
                <div className='mt-2'>
                  <FollowActionButton playerUuid={playerData.uuid} userUuid={user.uuid} />
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
